import { Building2, Inbox, Sprout, ScrollText, ChevronRight, ImageOff, Search, LayoutGrid, Upload, Bell, FileText, Files, Share2, Settings, ExternalLink, Sun, LogOut, CalendarDays, Users, Briefcase, ShieldCheck, Check } from "lucide-react";

/**
 * Sfondo del login: la dashboard REALE come componenti vivi (stesso markup e
 * stesse classi di Dashboard.tsx), NON uno screenshot.
 *
 * Sicurezza per costruzione:
 * - ZERO chiamate API: i dati sono demo hardcoded qui sotto. Prima del login
 *   nessun dato reale viaggia verso il client.
 * - Le cifre economiche sono OSCURATE (•) anche nei dati demo.
 * - Il wrapper in Login.tsx è inert + pointer-events-none: non si può
 *   interagire in alcun modo finché non si supera il TOTP.
 */

const DEMO_LISTINGS = [
  { id: "1", title: "Villa con piscina · Peschiera", slug: "villa-piscina-peschiera", city: "Peschiera del Garda", published: true },
  { id: "2", title: "Quadrilocale centro storico", slug: "quadrilocale-peschiera-centro", city: "Peschiera del Garda", published: true },
  { id: "3", title: "Villa vista lago · Lazise", slug: "villa-vista-lago-lazise", city: "Lazise", published: true },
  { id: "4", title: "Trilocale con giardino", slug: "trilocale-monzambano", city: "Monzambano", published: true },
  { id: "5", title: "Bilocale corte del Garda", slug: "bilocale-corte-del-garda", city: "Castelnuovo del Garda", published: false },
];

const DEMO_LEADS = [
  { id: "l1", name: "Markus Brandt", reason: "visita", email: "m.brandt@•••.de" },
  { id: "l2", name: "Hans Müller", reason: "offerta", email: "h.mueller@•••.at" },
];

const PRICE_MASK = "€ •••.•••";

const NAV = [
  { Icon: LayoutGrid, label: "Dashboard", active: true },
  { Icon: Building2, label: "Immobili" },
  { Icon: Upload, label: "Importa" },
  { Icon: Inbox, label: "Inbox lead" },
  { Icon: CalendarDays, label: "Agenda visite" },
  { Icon: Users, label: "Contatti" },
  { Icon: Briefcase, label: "Pratiche" },
  { Icon: ShieldCheck, label: "Adempimenti" },
  { Icon: Bell, label: "Alert acquirenti" },
  { Icon: FileText, label: "Blog" },
  { Icon: Files, label: "Pagine" },
];

// Vista urgenze demo: nomi/email mascherati come il resto del backdrop.
const DEMO_ATTENTION = [
  { id: "a1", days: 12, tone: "risk", Icon: Inbox, title: "Luca F.", detail: "Vendi casa a ••• senza risposta" },
  { id: "a2", days: 5, tone: "warn", Icon: Briefcase, title: "Vendita villa •••", detail: "Proposta in attesa di risposta" },
  { id: "a3", days: 2, tone: "neutral", Icon: Bell, title: "m.rossi@•••.it", detail: "2 immobili corrispondenti da proporre" },
] as const;
const NAV_SETUP = [
  { Icon: Share2, label: "Portali" },
  { Icon: Settings, label: "Team & setup" },
];

export default function LoginBackdrop(): JSX.Element {
  return (
    <div className="dot-grid min-h-screen bg-bg text-ink">
      {/* sidebar */}
      <aside className="fixed left-0 top-0 z-0 flex h-screen w-[240px] flex-col border-r border-rule bg-surface">
        <div className="flex items-center justify-between p-[16px_18px]">
          <span className="font-display text-[17px] font-bold tracking-tight">av<span className="text-accent">/</span>estate</span>
          <span className="text-ink-mute">«</span>
        </div>
        <nav className="flex-1 px-[10px]">
          <p className="micro p-[10px_10px_6px] uppercase tracking-wider text-ink-mute">Lavoro</p>
          {NAV.map(({ Icon, label, active }) => (
            <span key={label} className={`relative flex items-center gap-[11px] rounded-[7px] p-[9px_11px] text-[13.5px] ${active ? "bg-accent/10 text-ink" : "text-ink-soft"}`}>
              {active ? <span className="absolute -left-[10px] top-1/2 h-[18px] w-[3px] -translate-y-1/2 rounded-r bg-accent" /> : null}
              <Icon className={`size-[17px] ${active ? "text-accent" : "text-ink-mute"}`} />
              {label}
            </span>
          ))}
          <p className="micro p-[16px_10px_6px] uppercase tracking-wider text-ink-mute">Setup</p>
          {NAV_SETUP.map(({ Icon, label }) => (
            <span key={label} className="flex items-center gap-[11px] rounded-[7px] p-[9px_11px] text-[13.5px] text-ink-soft">
              <Icon className="size-[17px] text-ink-mute" />
              {label}
            </span>
          ))}
        </nav>
        <div className="border-t border-rule p-[14px_18px]">
          <span className="block truncate text-[13px]">admin@avestate.demo</span>
          <span className="micro text-ink-mute">OWNER</span>
        </div>
      </aside>

      {/* main */}
      <div className="pl-[240px]">
        {/* topbar */}
        <div className="flex h-[52px] items-center gap-[16px] border-b border-rule bg-surface px-[22px]">
          <span className="flex items-center gap-[9px] text-[13px] text-ink-mute"><Search className="size-4" /> Cerca immobili, inquiries, slug…</span>
          <span className="micro ml-[40px] text-ink-mute">CMD+K</span>
          <span className="ml-auto flex items-center gap-[18px] text-[13px] text-ink-soft">
            <span className="flex items-center gap-[7px]"><ExternalLink className="size-4" /> Vai al sito</span>
            <Sun className="size-4" />
            <span className="flex items-center gap-[7px]"><LogOut className="size-4" /> Esci</span>
          </span>
        </div>

        <main className="mx-auto w-full max-w-canvas px-9 py-8">
          <div className="space-y-[22px]">
            <header>
              <h1 className="font-display text-[27px] font-bold tracking-tight text-ink">Dashboard</h1>
              <p className="mt-[5px] text-[13.5px] text-ink-soft">Panoramica archivio av/estate.</p>
            </header>

            {/* stat strip — valore archivio OSCURATO */}
            <div className="grid grid-cols-2 overflow-hidden rounded-[5px] border border-rule bg-surface sm:grid-cols-4">
              <Stat v="8" l="Immobili" delta="+2 mese" />
              <Stat v="8" l="Pubblicati" />
              <Stat v="2" l="Lead aperti" />
              <Stat v="€ •,••" unit="M" l="Valore archivio" last />
            </div>

            <Panel
              ticks
              title="Nessuno resta indietro"
              action={
                <span className="flex items-center gap-[10px]">
                  <span className="micro text-risk">3 da seguire</span>
                  <span className="micro text-ink-mute">lead fermi da 3 gg</span>
                </span>
              }
            >
              <div className="py-[4px]">
                {DEMO_ATTENTION.map((item) => (
                  <div key={item.id} className="flex items-center gap-[12px] border-b border-rule-soft p-[10px_18px] last:border-b-0">
                    <span className={`inline-flex h-[22px] w-[52px] items-center justify-center rounded-full border font-mono text-[10.5px] ${item.tone === "risk" ? "border-risk/50 bg-risk/10 text-risk" : item.tone === "warn" ? "border-warn/50 bg-warn/10 text-warn" : "border-rule bg-surface-2 text-ink-soft"}`}>
                      {item.days} gg
                    </span>
                    <item.Icon className="size-[15px] shrink-0 text-ink-mute" />
                    <span className="min-w-0 flex-1 truncate text-[13px]">
                      <b className="font-semibold">{item.title}</b>
                      <span className="text-ink-soft"> · {item.detail}</span>
                    </span>
                    <span className="inline-flex h-[26px] items-center gap-[6px] whitespace-nowrap rounded-[5px] border border-rule bg-surface-2 px-[9px] text-[12px] text-ink-soft">
                      <Check className="size-[13px]" /> Segna gestito
                    </span>
                  </div>
                ))}
              </div>
            </Panel>

            <div className="grid grid-cols-1 gap-[18px] lg:grid-cols-2">
              <Panel ticks title="Collections" meta="navigazione">
                <CollCard Icon={Building2} name="Immobili" sub="archivio principale" big="8" />
                <CollCard Icon={Inbox} name="Inbox lead" sub="richieste in arrivo" big="2" border />
                <CollCard Icon={Sprout} name="Vendi casa" sub="lead valutazione" border />
                <CollCard Icon={ScrollText} name="Audit log" sub="6 eventi recenti" border />
              </Panel>

              <Panel title="Attività recente" meta="live">
                <div className="py-[6px]">
                  {DEMO_LEADS.map((i) => (
                    <div key={i.id} className="flex items-center gap-[12px] border-b border-rule-soft p-[11px_18px] last:border-b-0">
                      <span className="size-[7px] shrink-0 rounded-full bg-accent" />
                      <span className="min-w-0 text-[13px]">
                        <b className="font-semibold">{i.name}</b>
                        <span className="text-ink-soft"> · {i.reason}</span>
                      </span>
                      <span className="ml-auto truncate font-mono text-[11px] text-ink-mute">{i.email}</span>
                    </div>
                  ))}
                </div>
              </Panel>
            </div>

            <Panel ticks title="Ultimi immobili" action={<span className="micro">vedi tutti →</span>}>
              <table className="w-full border-collapse">
                <tbody>
                  {DEMO_LISTINGS.map((r) => (
                    <tr key={r.id} className="border-b border-rule-soft last:border-b-0">
                      <td className="p-[12px_18px]">
                        <span className="flex items-center gap-[13px]">
                          <span className="grid h-[40px] w-[54px] flex-none place-items-center rounded-[5px] border border-rule bg-surface-2 text-ink-mute"><ImageOff className="size-4" /></span>
                          <span className="min-w-0">
                            <span className="block truncate text-[13.5px] font-semibold text-ink">{r.title}</span>
                            <span className="block truncate font-mono text-[11px] text-ink-mute">{r.slug}</span>
                          </span>
                        </span>
                      </td>
                      <td className="p-[12px_18px]">
                        <span className="inline-flex h-[24px] items-center gap-[7px] rounded-full border border-rule bg-surface-2 px-[10px] text-[11.5px] text-ink-soft">
                          <span className={`size-[6px] rounded-full ${r.published ? "bg-success" : "bg-ink-mute"}`} />
                          {r.published ? "Pubblicato" : "Bozza"}
                        </span>
                      </td>
                      <td className="whitespace-nowrap p-[12px_18px] text-[13px] text-ink-soft">{r.city}</td>
                      <td className="whitespace-nowrap p-[12px_18px] text-right font-mono text-[13.5px] font-semibold text-ink">{PRICE_MASK}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Panel>
          </div>
        </main>
      </div>
    </div>
  );
}

function Stat({ v, unit, l, delta, last }: { v: string; unit?: string; l: string; delta?: string; last?: boolean }): JSX.Element {
  return (
    <div className={`p-[16px_18px] ${last ? "" : "border-r border-rule-soft"}`}>
      <div className="font-mono text-[25px] font-semibold tracking-tight text-ink">{v}{unit ? <span className="text-[14px] font-medium text-ink-mute">{unit}</span> : null}</div>
      <div className="micro mt-[5px]">{l}</div>
      {delta ? <div className="mt-[7px] text-[11px] text-success">↗ {delta}</div> : null}
    </div>
  );
}

function Panel({ title, meta, action, ticks, children }: { title: string; meta?: string; action?: React.ReactNode; ticks?: boolean; children: React.ReactNode }): JSX.Element {
  return (
    <div className={`relative rounded-[5px] border border-rule bg-surface ${ticks ? "ticks" : ""}`}>
      <div className="flex items-center gap-[10px] border-b border-rule p-[13px_16px]">
        <h3 className="text-[13px] font-semibold">{title}</h3>
        {meta ? <span className="micro ml-auto">{meta}</span> : null}
        {action ? <span className="ml-auto">{action}</span> : null}
      </div>
      {children}
    </div>
  );
}

function CollCard({ Icon, name, sub, big, border }: { Icon: typeof Building2; name: string; sub: string; big?: string; border?: boolean }): JSX.Element {
  return (
    <span className={`flex items-center gap-[14px] p-[16px_18px] ${border ? "border-t border-rule-soft" : ""}`}>
      <span className="grid size-[38px] place-items-center rounded-[8px] border border-rule bg-surface-2 text-accent"><Icon className="size-[18px]" /></span>
      <span className="min-w-0">
        <span className="block text-[14px] font-semibold">{name}</span>
        <span className="block font-mono text-[11px] text-ink-mute">{sub}</span>
      </span>
      {big ? <span className="ml-auto font-mono text-[22px] font-semibold">{big}</span> : <ChevronRight className="ml-auto size-4 text-ink-mute" />}
    </span>
  );
}
